import React, { useCallback, useMemo, useContext } from 'react';
import _ from 'lodash';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Stack, Button, Tooltip } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import GridSection from 'components/_layouts/GridSection';
import { useChildTemplates } from '../../../_shared/_data_sources/useChildTemplates';
import { useTemplateDetailsContext } from '../../../_shared/Details/context';
import PageToolbar from '_lex/_atoms/PageToolbar';
import { QuickSearch, ActionMenu } from 'components/_atoms';
import useUrlParams from 'hooks/useUrlParams';
import datetimeHelper from 'helpers/datetimeHelper';
import { AppContext } from 'views/App/context';
import useJurisdictions from 'data_sources/useJurisdictions';

export type ChildTemplateListParams = {
  search?: string | null;
};

const defaultParams: ChildTemplateListParams = {
  search: null,
};

const ChildTemplates: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const { template } = useTemplateDetailsContext();
  const [params, setParams] = useUrlParams(defaultParams);
  const jurisdictionsQuery = useJurisdictions();

  // Check if parent template is active
  const isParentActive = template?.status === 'Active';

  const updateParams = useCallback(
    (updatedParams: ChildTemplateListParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const queryParams = useMemo(() => {
    const cleanParams = _.omitBy({ ...params }, _.isEmpty);
    return cleanParams;
  }, [params]);

  const childTemplatesQuery = useChildTemplates(Number(id), queryParams);
  const allChildTemplates = childTemplatesQuery.data?.pages.flatMap((page) => page.content) || [];
  const totalCount = childTemplatesQuery.data?.pages[0]?.count || 0;
  const { isLoading } = childTemplatesQuery;

  const canManageTemplates = hasPermission('manageFieldBank', currentOrganizationId || '');

  const onSearchChange = useCallback(
    (search: string) => {
      updateParams({ search: search || null });
    },
    [updateParams]
  );

  const formatDate = (dateString?: string | null) => {
    return datetimeHelper.formatDate(dateString, { format: 'short', missingDisplay: '-' });
  };

  // Find the jurisdiction name
  const getJurisdictionName = useCallback(
    (jurisdictionId: string) => {
      const jurisdiction = jurisdictionsQuery.data?.find((j: any) => j.id === jurisdictionId);
      return jurisdiction ? `${jurisdiction.name} (${jurisdiction.abbreviation})` : '-';
    },
    [jurisdictionsQuery.data]
  );

  const columns = useMemo(
    () => ({
      jurisdiction: {
        header: t('performanceReporting.templates.details.childTemplates.columns.jurisdiction'),
        flex: 1.5,
        renderer: (params: any) => (
          <Typography variant='body2'>
            {params.row.jurisdictionId ? getJurisdictionName(params.row.jurisdictionId) : '-'}
          </Typography>
        ),
      },
      name: {
        header: t('performanceReporting.templates.details.childTemplates.columns.name'),
        flex: 2,
        renderer: (params: any) => (
          <Typography
            variant='body2'
            fontWeight='medium'
          >
            {params.row.name}
          </Typography>
        ),
      },
      createdBy: {
        header: t('performanceReporting.templates.details.childTemplates.columns.author'),
        flex: 1.5,
        renderer: (params: any) => <Typography variant='body2'>{params.row.author || '-'}</Typography>,
      },
      effectiveDate: {
        header: t('performanceReporting.templates.details.childTemplates.columns.effectiveDate'),
        flex: 1.5,
        renderer: (params: any) => <Typography variant='body2'>{formatDate(params.row.effectiveDate)}</Typography>,
      },
      lastPublishDate: {
        header: t('performanceReporting.templates.details.childTemplates.columns.lastPublished'),
        flex: 1.5,
        renderer: (params: any) => <Typography variant='body2'>{formatDate(params.row.lastPublished)}</Typography>,
      },
      actions: {
        header: '',
        flex: 0.5,
        renderer: (params: any) => (
          <ActionMenu
            id={`child-template-actions-${params.row.id}`}
            items={[
              {
                key: 'details',
                label: t('performanceReporting.templates.details.childTemplates.actions.templateDetails'),
                onClick: () => navigate(`/admin/performance-reporting/templates/details/${params.row.id}`),
              },
              ...(canManageTemplates
                ? [
                    {
                      key: 'edit',
                      label: t('performanceReporting.templates.details.childTemplates.actions.editDraft'),
                      onClick: () => navigate(`/admin/performance-reporting/templates/builder/${params.row.id}`),
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    }),
    [t, canManageTemplates, getJurisdictionName]
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
      <PageToolbar
        leftItems={
          <Stack
            direction='row'
            spacing={3}
            sx={{
              alignItems: 'center',
              '& > *': {
                minWidth: 'fit-content',
                flexShrink: 0,
              },
            }}
          >
            <QuickSearch
              id='child_templates_search'
              initialValue={params.search || ''}
              onChange={onSearchChange}
              placeholder={t('performanceReporting.templates.details.childTemplates.search.placeholder')}
            />
          </Stack>
        }
        rightItems={
          canManageTemplates && (
            <Tooltip
              title={
                !isParentActive
                  ? t('performanceReporting.templates.details.childTemplates.createButtonDisabledTooltip')
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={!isParentActive}
                  onClick={() => navigate(`/admin/performance-reporting/templates/new?parentId=${id}`)}
                >
                  {t('performanceReporting.templates.details.childTemplates.createButton')}
                </Button>
              </span>
            </Tooltip>
          )
        }
      />
      {isLoading ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <GridSection
          columns={columns}
          data={allChildTemplates}
          totalCount={totalCount}
          query={childTemplatesQuery}
          gridKey={JSON.stringify(queryParams)}
        />
      )}
    </Box>
  );
};

export default ChildTemplates;
